import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Flame,
  ShieldAlert,
  TrendingUp,
  AlertTriangle,
  ExternalLink,
  Calendar,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import FeedbackButton from "../components/FeedbackButton";
import { fetchWhatsBroken } from "../lib/api";

export default function WhatsBrokenPage() {
  const { date: dateParam } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    fetchWhatsBroken(dateParam)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e?.response?.data?.detail || e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [dateParam]);

  const openAnalysis = (cveId) => {
    nav(`/?cve=${encodeURIComponent(cveId)}`);
  };

  return (
    <div className="min-h-screen text-slate-900 bg-slate-50">
      <Toaster theme="light" position="top-right" />

      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex items-center gap-6">
          <Link
            to="/"
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-slate-700 hover:text-violet-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-display font-bold tracking-tight">
              CVE Analytics
            </span>
          </Link>
          <div className="overline hidden sm:block">Today . What broke</div>
          <div className="flex-1" />
          <FeedbackButton variant="header" />
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-8 md:py-12 space-y-8">
        <Hero data={data} loading={loading} err={err} />

        {data && (
          <>
            <Section
              testid="kev-section"
              icon={Flame}
              iconClass="text-red-700"
              title="Actively exploited"
              subtitle="Just added to the CISA KEV catalog"
              count={data.summary.kev_added}
              empty="No new KEV additions in this window."
            >
              {data.kev_additions.map((k) => (
                <KevRow key={k.cve_id} row={k} onAnalyze={openAnalysis} />
              ))}
            </Section>

            <Section
              testid="fresh-section"
              icon={ShieldAlert}
              iconClass="text-amber-700"
              title="Fresh criticals"
              subtitle="Published in this window with CVSS greater than or equal to 7"
              count={data.summary.fresh_criticals}
              empty="No fresh critical CVEs in this window."
            >
              {data.fresh_criticals.map((c) => (
                <FreshRow key={c.cve_id} row={c} onAnalyze={openAnalysis} />
              ))}
            </Section>

            <Section
              testid="movers-section"
              icon={TrendingUp}
              iconClass="text-violet-700"
              title="Climbing fast"
              subtitle="EPSS percentile jumped 20 points or more overnight"
              count={data.summary.epss_movers}
              empty="Movers need two consecutive daily snapshots. Come back tomorrow."
            >
              {data.epss_movers.map((m) => (
                <MoverRow key={m.cve_id} row={m} onAnalyze={openAnalysis} />
              ))}
            </Section>
          </>
        )}

        <footer className="pt-8 pb-4 border-t border-slate-100 text-xs text-slate-500 font-mono flex flex-wrap items-center gap-3">
          <Calendar className="h-3.5 w-3.5" />
          <span>
            Data: CISA KEV catalog . NVD publications . EPSS daily snapshots
          </span>
          {data && (
            <span className="text-slate-500">
              . generated {new Date(data.generated_at).toLocaleString()}
            </span>
          )}
        </footer>
      </main>
    </div>
  );
}

function Hero({ data, loading, err }) {
  if (loading) {
    return (
      <div className="animate-pulse space-y-3" data-testid="whats-broken-loading">
        <div className="h-8 w-56 bg-slate-100 rounded" />
        <div className="h-6 w-96 bg-slate-100 rounded" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="panel border border-red-200 bg-red-50 p-5 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-700 shrink-0 mt-1" />
        <div>
          <div className="overline text-red-700">Digest unavailable</div>
          <div className="text-sm text-slate-700 mt-1">{err}</div>
        </div>
      </div>
    );
  }
  if (!data) return null;
  const s = data.summary;
  const parts = [
    `${s.kev_added} KEV addition${s.kev_added === 1 ? "" : "s"}`,
    `${s.fresh_criticals} fresh critical${s.fresh_criticals === 1 ? "" : "s"}`,
    `${s.epss_movers} EPSS mover${s.epss_movers === 1 ? "" : "s"}`,
  ];
  const window_days = data.window_days || 1;
  const end = new Date(data.date + "T00:00:00Z").toLocaleDateString(undefined, {
    month: "long", day: "numeric", year: "numeric",
  });
  const start = data.window_start
    ? new Date(data.window_start + "T00:00:00Z").toLocaleDateString(undefined, {
        month: "long", day: "numeric",
      })
    : null;
  const title = window_days > 1
    ? `What broke in the last ${window_days} days`
    : `What broke on ${new Date(data.date + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`;
  return (
    <div data-testid="whats-broken-hero">
      <div className="overline text-violet-700">Daily digest</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold mt-2 tracking-tight">
        {window_days > 1 ? (
          <>What broke in the <span className="text-violet-700">last {window_days} days</span></>
        ) : (
          <>What broke on <span className="text-violet-700">{end}</span></>
        )}
      </h1>
      <p className="text-slate-700 mt-3 font-mono text-sm">
        {window_days > 1 && start && (
          <>
            <span className="text-slate-500">{start} to {end}</span>
            <span className="mx-2 text-slate-300">.</span>
          </>
        )}
        {parts.join("  .  ")}
        {s.ransomware_flagged > 0 && (
          <span className="ml-3 text-red-700">
            . {s.ransomware_flagged} flagged for ransomware use
          </span>
        )}
      </p>
    </div>
  );
}

function Section({ testid, icon: Icon, iconClass, title, subtitle, count, empty, children }) {
  return (
    <section data-testid={testid} className="panel p-5 md:p-7">
      <div className="flex items-start gap-3 mb-5">
        <div className={`h-9 w-9 rounded-md border border-slate-200 bg-slate-100 grid place-items-center shrink-0`}>
          <Icon className={`h-4 w-4 ${iconClass}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-xl font-bold text-slate-900">
              {title}
            </h2>
            <span className="font-mono text-xs text-slate-500">{count}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
        </div>
      </div>
      {count === 0 ? (
        <div className="text-xs font-mono text-slate-500 py-4">{empty}</div>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </section>
  );
}

function KevRow({ row, onAnalyze }) {
  return (
    <div
      data-testid={`kev-row-${row.cve_id}`}
      className="border border-slate-100 hover:border-violet-300 rounded-md p-3 flex flex-col md:flex-row md:items-center gap-3 group transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onAnalyze(row.cve_id)}
            className="font-mono text-sm text-slate-900 hover:text-violet-700 transition-colors"
          >
            {row.cve_id}
          </button>
          {row.ransomware && (
            <span className="text-[10px] font-mono uppercase tracking-wider text-red-700 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-sm">
              ransomware
            </span>
          )}
          <span className="text-xs text-slate-600">
            {row.vendor} . {row.product}
          </span>
        </div>
        <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
          {row.name}
        </p>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          added {row.date_added} . due by {row.due_date}
        </p>
      </div>
      <button
        onClick={() => onAnalyze(row.cve_id)}
        data-testid={`analyze-${row.cve_id}`}
        className="flex items-center gap-1.5 text-xs font-mono text-violet-700 hover:text-violet-800 border border-violet-200 hover:border-violet-300 bg-violet-50 hover:bg-violet-100 rounded-md px-3 py-1.5 transition-colors shrink-0"
      >
        Analyze
        <ExternalLink className="h-3 w-3" />
      </button>
    </div>
  );
}

function FreshRow({ row, onAnalyze }) {
  const sev = (row.severity || "").toUpperCase();
  const sevClass = {
    CRITICAL: "text-red-700 border-red-200 bg-red-50",
    HIGH: "text-orange-700 border-orange-200 bg-orange-50",
    MEDIUM: "text-amber-700 border-amber-200 bg-amber-50",
  }[sev] || "text-slate-600 border-slate-200 bg-slate-100";
  return (
    <div
      data-testid={`fresh-row-${row.cve_id}`}
      className="border border-slate-100 hover:border-violet-300 rounded-md p-3 flex flex-col md:flex-row md:items-center gap-3 transition-colors"
    >
      <div className="shrink-0 w-14 text-center">
        <div className="font-display font-bold text-2xl leading-none text-slate-900">
          {row.score}
        </div>
        <div className={`text-[10px] font-mono uppercase tracking-wider mt-1 border rounded-sm px-1 py-0.5 ${sevClass}`}>
          {sev}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <button
          onClick={() => onAnalyze(row.cve_id)}
          className="font-mono text-sm text-slate-900 hover:text-violet-700 transition-colors"
        >
          {row.cve_id}
        </button>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed line-clamp-2">
          {row.description}
        </p>
        <p className="text-[11px] text-slate-500 mt-1 font-mono">
          {row.attack_vector && `AV:${row.attack_vector}  .  `}
          published {(row.published || "").slice(0, 19).replace("T", " ")}
        </p>
      </div>
      <button
        onClick={() => onAnalyze(row.cve_id)}
        data-testid={`analyze-${row.cve_id}`}
        className="flex items-center gap-1.5 text-xs font-mono text-violet-700 hover:text-violet-800 border border-violet-200 hover:border-violet-300 bg-violet-50 hover:bg-violet-100 rounded-md px-3 py-1.5 transition-colors shrink-0"
      >
        Analyze
        <ExternalLink className="h-3 w-3" />
      </button>
    </div>
  );
}

function MoverRow({ row, onAnalyze }) {
  const pctNow = Math.round(row.percentile_now * 100);
  const pctPrev = Math.round(row.percentile_prev * 100);
  const delta = Math.round(row.delta * 100);
  return (
    <div
      data-testid={`mover-row-${row.cve_id}`}
      className="border border-slate-100 hover:border-violet-300 rounded-md p-3 flex items-center gap-3 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <button
          onClick={() => onAnalyze(row.cve_id)}
          className="font-mono text-sm text-slate-900 hover:text-violet-700 transition-colors"
        >
          {row.cve_id}
        </button>
        <p className="text-xs text-slate-600 mt-1 font-mono">
          EPSS {pctPrev}th to <span className="text-violet-700">{pctNow}th</span>{" "}
          percentile
          <span className="text-emerald-700 ml-2">+{delta} points</span>
        </p>
      </div>
      <button
        onClick={() => onAnalyze(row.cve_id)}
        data-testid={`analyze-${row.cve_id}`}
        className="flex items-center gap-1.5 text-xs font-mono text-violet-700 hover:text-violet-800 border border-violet-200 hover:border-violet-300 bg-violet-50 hover:bg-violet-100 rounded-md px-3 py-1.5 transition-colors shrink-0"
      >
        Analyze
        <ExternalLink className="h-3 w-3" />
      </button>
    </div>
  );
}
